import { TriangleAlert } from "lucide-react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate, useRouteError } from "react-router";

import { useAuth } from "@/auth/use-auth";
import { CopyReferenceButton } from "@/components/CopyReferenceButton";
import { Button } from "@/components/ui/button";
import { isApiErrorLike } from "@/lib/api/errors";

import { reportError } from "./monitoring";

/**
 * Route error boundary (react-router `errorElement`, T11-06): the broken screen is replaced by a
 * plain "something went wrong" page with Try again / Home — the shell (menu) keeps working —
 * and the error goes to GlitchTip with the request id, route and staff id.
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { session } = useAuth();
  const requestId = isApiErrorLike(error) && error.requestId ? error.requestId : null;
  const staffId = session?.user.id ?? null;

  useEffect(() => {
    reportError(error, {
      route: location.pathname,
      staffId,
      requestId,
      code: isApiErrorLike(error) ? error.code : undefined,
    });
  }, [error, location.pathname, staffId, requestId]);

  return (
    <section
      role="alert"
      data-slot="route-error"
      className="flex flex-col items-start gap-3 rounded-lg border p-6"
    >
      <TriangleAlert className="size-8 text-status-problem" aria-hidden />
      <h1 className="text-xl font-bold">{t("errors.boundary_title")}</h1>
      <p className="text-muted-foreground">{t("errors.boundary_body")}</p>
      {requestId ? <CopyReferenceButton requestId={requestId} /> : null}
      <div className="flex flex-wrap gap-2">
        <Button
          onClick={() => {
            // same-URL navigation clears the route error without reloading the app (R21)
            void navigate(location.pathname + location.search, { replace: true });
          }}
        >
          {t("errors.try_again")}
        </Button>
        <Button asChild variant="outline">
          <Link to="/">{t("errors.back_home")}</Link>
        </Button>
      </div>
    </section>
  );
}
